import React, { type ReactNode, useRef } from 'react';
import {
  TextField as TextFieldRAC,
  Input as InputRAC,
} from 'react-aria-components';

import { useFocusFieldEvent } from '../../../contexts/events-context';
import { useFieldAttributes } from '../../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../../contexts/field-props-context';
import { type MlaAddressLine2Props } from '../../../fields/mla-address-line-2/mla-address-line-2/mla-address-line-2-props';
import { useAddressLabel } from '../../../hooks/use-address-label';
import {
  findChildOfType,
  applyInternalProps,
} from '../../../services/find-child';
import { Description } from '../../field-description/field-description-headless';
import { Error } from '../../field-error/field-error-headless';
import {
  Label,
  type LabelInternalProps,
} from '../../field-label/field-label-headless';

export type AddressLine2Props = {
  children: ReactNode;
};

export const AddressLine2 = ({ children }: AddressLine2Props) => {
  const {
    id,
    value,
    onChange,
    disabled,
    required,
    hasError,
    onBlur,
    onFocus,
    FieldLayout,
  } = useFieldPropsV2<MlaAddressLine2Props>();

  const addressLine2Label = useAddressLabel('addressLine2');
  const labelElement = findChildOfType(children, AddressLine2.Label);
  const inputElement = findChildOfType(children, AddressLine2.Input);
  const descriptionElement = findChildOfType(
    children,
    AddressLine2.Description,
  );
  const errorElement = findChildOfType(children, AddressLine2.Error);

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() =>
        applyInternalProps<LabelInternalProps>(labelElement, {
          label: addressLine2Label,
        })
      }
      renderInput={() => (
        <TextFieldRAC
          value={value ?? ''}
          onChange={onChange}
          isDisabled={disabled}
          isRequired={required}
          isInvalid={hasError}
          onBlur={onBlur}
          onFocus={onFocus}
        >
          {inputElement}
        </TextFieldRAC>
      )}
      renderDescription={() => (
        <>
          {descriptionElement}
          {errorElement}
        </>
      )}
    />
  );
};

type InputProps = {
  className?: string;
};

const Input = ({ className }: InputProps) => {
  const { id } = useFieldPropsV2<MlaAddressLine2Props>();
  const { inputId, ariaDescribedBy } = useFieldAttributes();

  const inputRef = useRef<HTMLInputElement>(null);

  useFocusFieldEvent(() => {
    inputRef.current?.focus();
  }, id);

  return (
    <InputRAC
      ref={inputRef}
      id={inputId}
      aria-describedby={ariaDescribedBy}
      className={className}
    />
  );
};

AddressLine2.Label = Label;
AddressLine2.Input = Input;
AddressLine2.Description = Description;
AddressLine2.Error = Error;
